import Forum from "messaging/forums/forum";

const chatTypeLabels: Record<string, string> = {
  NetworkConnection: "Network connection",
  UserNetworkConnection: "Network connection",
  "Networked.Group": "Network group",
  "Marketplace.Individual": "Individual",
  "Marketplace.Company": "Company",
  "Marketplace.Team": "Team",
  "Work.Proposal": "Proposal",
  "Work.Agreement": "Work agreement",
  userCreatedChat: "Conversation",
};

const networkConnectionEntityClasses = new Set(["NetworkConnection", "UserNetworkConnection"]);

/**
 * Why the reader cannot write in a forum, said where the message editor would be, or undefined when
 * they can. Somebody who left the work reads it as it was when they left; the chats of work that has
 * ended are a record for everybody (#5833).
 */
export function getForumReadOnlyNotice(forum: Forum | undefined): string | undefined {
  if (forum?.visibleUntil) {
    return `You left this work on ${forum.visibleUntil.format("MM/DD/YYYY")}. You can read what was said until then.`;
  }
  if (forum?.acceptsMessages === false) {
    return "This work has ended. Its chats are kept as a record and take no new messages.";
  }
  if (forum?.callerMayWrite === false) {
    return "You can read this chat. You can write in it once you are on the work.";
  }
  return undefined;
}

export function getForumChatTypeLabel(forum: Forum): string {
  const entityClass = forum.topic?.entityClass?.value;
  if (!entityClass) return "Conversation";

  return chatTypeLabels[entityClass] ?? humanizeChatType(entityClass);
}

/**
 * What a conversation row says under its title: what the conversation is about, and — for a topic
 * that has one — the stage that topic is at, so a proposal thread says where its proposal stands
 * without the reader opening it (#5129).
 *
 * A work agreement or proposal also says which version of the work it is, "v2" or "v3 (proposed)",
 * because a change order keeps its project's name and its conversation would otherwise read the
 * same as the earlier version's (#6809).
 */
export function getForumChatSubtitle(
  forum: Forum,
  entityName?: string,
  currentUserName?: string,
  entityStatus?: string,
  entityVersion?: string,
): string {
  const { lead, trail } = getForumChatSubtitleParts(forum, entityName, currentUserName, entityStatus, entityVersion);
  return trail ? `${lead} · ${trail}` : lead;
}

/**
 * The same subtitle in two halves, for a row too narrow to show all of it: `lead` is what the
 * conversation is about and may be cut short, `trail` is the version and stage, which may not.
 * Cut from the end, "Work agreement · Trademark renewal · v2" loses the "v2" first, and the
 * version is the one part that tells a change order's conversation from the earlier version's.
 */
export function getForumChatSubtitleParts(
  forum: Forum,
  entityName?: string,
  currentUserName?: string,
  entityStatus?: string,
  entityVersion?: string,
): { lead: string; trail?: string } {
  const segments = [getForumChatTypeLabel(forum)];

  if (entityName) {
    // The name earns its place only when the row is not already titled with it.
    const title = getForumChatTitle(forum, currentUserName);
    if (title.trim().toLocaleLowerCase() !== entityName.trim().toLocaleLowerCase()) segments.push(entityName);
  }

  // The version stays when the name is dropped as a repeat of the title: the title has no version.
  const trailing = [entityVersion, entityStatus].filter((segment): segment is string => Boolean(segment));

  return { lead: segments.join(" · "), trail: trailing.length > 0 ? trailing.join(" · ") : undefined };
}

export function getForumChatTitle(forum: Forum, currentUserName?: string): string {
  const entityClass = forum.topic?.entityClass?.value;
  let title = cleanGeneratedForumTitle(forum.name.toString().trim(), entityClass);

  if (networkConnectionEntityClasses.has(entityClass ?? "")) {
    title = title.replace(/\s+connection chat\s*$/i, "");
  }

  const separator = networkConnectionEntityClasses.has(entityClass ?? "") ? /\s*<=>\s*|\s*,\s*/ : /\s*,\s*/;
  const participants = title.split(separator).map((participant) => participant.trim()).filter(Boolean);
  const filteredParticipants = participants.filter((participant) => participant !== currentUserName);

  return filteredParticipants.join(", ") || title;
}

function cleanGeneratedForumTitle(title: string, entityClass?: string): string {
  if (entityClass === "Work.Proposal") {
    const proposalCommentThreadMatch = title.match(
      /^Proposal\s+(?:clientReviewers|vendorReviewers|allReviewers)\s+(.+?)\s+comment thread$/i,
    );
    if (proposalCommentThreadMatch?.[1]) return proposalCommentThreadMatch[1].trim();

    return title
      .replace(/^(?:Proposal|Project):\s*/i, "")
      .replace(/\s+general\s+comment thread\s*$/i, "")
      .replace(/\s+comment thread\s*$/i, "")
      .trim();
  }

  if (entityClass === "Marketplace.Company") {
    return title.replace(/^Contact with\s+/i, "").trim();
  }

  if (entityClass === "Work.Agreement") {
    return title.replace(/^Work Agreement:\s*/i, "").replace(/^Agreement:\s*/i, "").trim();
  }

  return title;
}

function humanizeChatType(entityClass: string): string {
  const typeName = entityClass.split(".").at(-1) ?? entityClass;
  const humanized = typeName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return humanized ? humanized.charAt(0).toUpperCase() + humanized.slice(1) : "Conversation";
}
